// @eidollon/contracts/api
//
// API ENTITY CONTRACT — locked Day 0 of Wave 3.
//
// These are the SHAPES all three lanes share:
//   * services/api emits them (all rows go through these schemas before being
//     returned to the client)
//   * apps/terminal consumes them (no untyped fetch calls in the SPA)
//
// Conventions:
//   * Money is always { value: number, currency: string } where currency is
//     ISO 4217. The value is in the smallest unit of the currency (cents/pence)
//     for SQL storage, but exposed in the API in the full unit (dollars). The
//     server does the conversion in one place.
//   * Dates are ISO 8601 strings (never Date objects across the wire).
//   * Pagination is cursor-based for grids (server-side row model).
//   * Every entity has a stable `kind` discriminator so the command palette
//     and the world map can route uniformly.

import { z } from 'zod';

/* ──────────────────────────────────────────────────────────────────────────
 *  Shared
 * ────────────────────────────────────────────────────────────────────────── */

export const ISODate = z.string().min(10);
export const Iso2 = z.string().length(2);
export const Money = z.object({
  value: z.number(), // dollars (or relevant unit)
  currency: z.string().length(3), // ISO 4217
});

export const PageRequest = z.object({
  cursor: z.string().nullish(),
  limit: z.number().int().min(1).max(100).default(50),
});

export const PageResponse = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    next_cursor: z.string().nullable(),
    total: z.number().int().nullable(), // null when COUNT(*) is too expensive to compute
  });

export const EntityKind = z.enum([
  'opp', // tender / opportunity
  'award',
  'contract',
  'vendor',
  'agency',
  'case',
  'bid',
  'watchlist',
  'document',
  'standing_offer',
  'disclosure',
]);
export type EntityKind = z.infer<typeof EntityKind>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Opportunity (tender)
 * ────────────────────────────────────────────────────────────────────────── */

export const OpportunityStatus = z.enum([
  'active',
  'expired',
  'cancelled',
  'awarded',
  'draft',
  'unknown',
]);

export const OpportunityRow = z.object({
  id: z.string().uuid(),
  publication_date: ISODate,
  jurisdiction: Iso2,
  source_id: z.string(),
  source_record_id: z.string(),
  ocid: z.string().nullable(),
  tender_id: z.string().nullable(),
  reference_number: z.string().nullable(),
  status: OpportunityStatus,
  notice_type: z.string().nullable(),
  procurement_category: z.string().nullable(),
  procurement_method: z.string().nullable(),
  title: z.string().nullable(), // resolved title (en > fr)
  title_en: z.string().nullable(),
  title_fr: z.string().nullable(),
  agency_raw_name: z.string().nullable(),
  agency_id: z.string().uuid().nullable(),
  closing_date: z.string().nullable(), // ISO timestamp
  amendment_date: ISODate.nullable(),
  contract_value: Money.nullable(),
  // Tender's own published value (migration 0057). estimated_value is the
  // awarded value on award notices. currency is ISO-4217.
  estimated_value: z.number().nullable().default(null),
  currency: z.string().nullable().default(null),
  // Redistribution rights (migration 0056). false = resold/commercial source
  // held back from tenant export until its ToS is cleared.
  redistributable: z.boolean().default(true),
  // Classification arrays are jsonb passthrough: string[] on legacy rows,
  // {code, description}[] on OCDS/eForms rows. The cell renders either shape.
  unspsc_codes: z.array(z.unknown()).default([]),
  cpv_codes: z.array(z.unknown()).default([]),
  naics_codes: z.array(z.unknown()).default([]),
  notice_url_en: z.string().nullable().default(null),
  notice_url_fr: z.string().nullable().default(null),
  trade_agreements: z.string().nullable(),
  regions_of_delivery: z.string().nullable(),
  // computed
  days_to_close: z.number().int().nullable(),
});
export type OpportunityRow = z.infer<typeof OpportunityRow>;

export const OpportunityDetail = OpportunityRow.extend({
  description_en: z.string().nullable(),
  description_fr: z.string().nullable(),
  selection_criteria_en: z.string().nullable(),
  contact_name: z.string().nullable(),
  contact_email: z.string().nullable(),
  org_name: z.string().nullable(),
  org_address: z.string().nullable(),
  notice_url_en: z.string().nullable(),
  notice_url_fr: z.string().nullable(),
  raw: z.unknown(), // jsonb passthrough
});

export const OpportunityListQuery = z
  .object({
    q: z.string().optional(),
    status: OpportunityStatus.optional(),
    jurisdiction: Iso2.optional(),
    agency_id: z.string().uuid().optional(),
    category: z.string().optional(),
    closing_from: ISODate.optional(),
    closing_to: ISODate.optional(),
    value_min: z.number().optional(),
    value_max: z.number().optional(),
    set_aside: z.string().optional(),
    sort: z
      .enum(['closing_asc', 'closing_desc', 'value_desc', 'relevance', 'newest'])
      .default('closing_asc'),
  })
  .merge(PageRequest);

/* ──────────────────────────────────────────────────────────────────────────
 *  Award
 * ────────────────────────────────────────────────────────────────────────── */

export const AwardRow = z.object({
  id: z.string().uuid(),
  publication_date: ISODate,
  jurisdiction: Iso2,
  solicitation_number: z.string().nullable(),
  contract_number: z.string().nullable(),
  status: z.string(),
  title: z.string().nullable(),
  vendor_name: z.string().nullable(),
  vendor_id: z.string().uuid().nullable(),
  agency_raw_name: z.string().nullable(),
  agency_id: z.string().uuid().nullable(),
  award_date: ISODate.nullable(),
  start_date: ISODate.nullable(),
  end_date: ISODate.nullable(),
  contract_value: Money.nullable(),
  total_contract_value: Money.nullable(),
  procurement_category: z.string().nullable(),
  procurement_method: z.string().nullable(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Contract
 * ────────────────────────────────────────────────────────────────────────── */

export const ContractRow = AwardRow.extend({
  procurement_number: z.string().nullable(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Vendor
 * ────────────────────────────────────────────────────────────────────────── */

export const VendorRow = z.object({
  id: z.string().uuid().nullable(), // null = pending entity resolution
  name: z.string(), // raw name when not yet canonicalized
  jurisdiction: Iso2.nullable(),
  total_spend: Money.nullable(),
  contract_count: z.number().int(),
  award_count: z.number().int(),
  pending_resolution: z.boolean(), // drives the empty-state pattern on /vendor/:id
});

export const VendorDetail = VendorRow.extend({
  legal_name: z.string().nullable(),
  operating_name: z.string().nullable(),
  aliases: z.array(z.string()),
  identifiers: z.array(
    z.object({
      kind: z.string(), // CAGE, DUNS, BN9, ...
      value: z.string(),
    }),
  ),
  top_agencies: z.array(
    z.object({
      agency_id: z.string().uuid().nullable(),
      name: z.string(),
      spend: Money,
      count: z.number().int(),
    }),
  ),
  spend_by_year: z.array(
    z.object({
      year: z.number().int(),
      spend: Money,
    }),
  ),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Agency
 * ────────────────────────────────────────────────────────────────────────── */

export const AgencyRow = z.object({
  id: z.string().uuid().nullable(),
  name: z.string(),
  jurisdiction: Iso2.nullable(),
  abbreviation: z.string().nullable(),
  tender_count: z.number().int(),
  award_count: z.number().int(),
  contract_count: z.number().int(),
  total_spend: Money.nullable(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Case + Bid + Decision
 * ────────────────────────────────────────────────────────────────────────── */

export const CaseClassification = z.enum(['PUBLIC', 'CUI', 'SECRET', 'TS', 'TS_SCI']);
export const CaseStatus = z.enum(['open', 'active', 'archived', 'closed']);

export const CaseRow = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  classification: CaseClassification,
  cage_code: z.string().nullable(),
  banner_label: z.string().nullable(),
  status: CaseStatus,
  created_at: z.string(),
  updated_at: z.string(),
});

export const BidStatus = z.enum([
  'CAPTURE',
  'DRAFTING',
  'REVIEW',
  'SUBMITTED',
  'WON',
  'LOST',
  'NO_BID',
  'WITHDRAWN',
]);

export const BidRow = z.object({
  id: z.string().uuid(),
  code: z.string(),
  title: z.string(),
  status: BidStatus,
  case_id: z.string().uuid().nullable(),
  opportunity_id: z.string().uuid().nullable(),
  due_at: z.string().nullable(),
  win_probability_self: z.number().int().nullable(),
  page_count_target: z.number().int().nullable(),
  page_count_current: z.number().int(),
  compliance_pct: z.number().int().nullable(),
  draft_revision: z.number().int(),
  updated_at: z.string(),
});

export const BidSection = z.object({
  id: z.string().uuid(),
  bid_id: z.string().uuid(),
  volume: z.string(),
  section: z.string(),
  ordinal: z.number().int(),
  title: z.string().nullable(),
  content_md: z.string(),
  content_revision: z.number().int(),
  word_count: z.number().int(),
  page_count: z.number().int(),
  last_edited_at: z.string(),
});

export const DecisionKind = z.enum([
  'SUGGEST',
  'GAP',
  'RISK',
  'CITE',
  'REWRITE',
  'COMPLIANCE',
  'PRICING',
  'SUMMARY',
]);
export const DecisionStatus = z.enum(['open', 'accepted', 'rejected', 'superseded']);

export const Decision = z.object({
  id: z.string().uuid(),
  bid_id: z.string().uuid(),
  bid_section_id: z.string().uuid().nullable(),
  agent_id: z.string(),
  agent_version: z.string().nullable(),
  kind: DecisionKind,
  title: z.string(),
  body_md: z.string(),
  bullets: z.array(z.string()),
  evidence: z.array(
    z.object({
      type: EntityKind,
      id: z.string(),
      label: z.string().optional(),
    }),
  ),
  confidence: z.number().min(0).max(1).nullable(),
  status: DecisionStatus,
  created_at: z.string(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Stats / overview
 * ────────────────────────────────────────────────────────────────────────── */

export const StatsOverview = z.object({
  totals: z.object({
    tenders: z.number().int(),
    awards: z.number().int(),
    contracts: z.number().int(),
    vendors: z.number().int(),
    agencies: z.number().int(),
    standing_offers: z.number().int(),
    disclosures: z.number().int(),
  }),
  by_jurisdiction: z.array(
    z.object({
      jurisdiction: Iso2,
      count: z.number().int(),
    }),
  ),
  active_now: z.number().int(),
  fy_pipeline_total: Money.nullable(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Map
 * ────────────────────────────────────────────────────────────────────────── */

export const MapPoint = z.object({
  id: z.string(),
  kind: EntityKind,
  lat: z.number(),
  lng: z.number(),
  title: z.string(),
  subtitle: z.string().nullable(),
  value: z.number().nullable(), // for sizing / sorting
  ts: z.string().nullable(),
  href: z.string().nullable(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Audit
 * ────────────────────────────────────────────────────────────────────────── */

export const AuditEvent = z.object({
  event_id: z.string().uuid(),
  ts: z.string(),
  actor_id: z.string().uuid().nullable(),
  actor_label: z.string(),
  action_class: z.enum(['DATA', 'AI', 'BID', 'AUTH', 'CASE', 'ADMIN']),
  action: z.string(),
  resource_type: z.string().nullable(),
  resource_id: z.string().nullable(),
  result: z.enum(['success', 'failure', 'blocked']),
  ip: z.string().nullable(),
  request_id: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()).nullable(),
});

/* ──────────────────────────────────────────────────────────────────────────
 *  Intel (mock-backed in Wave 3)
 * ────────────────────────────────────────────────────────────────────────── */

export const IntelKind = z.enum(['sanction', 'conflict', 'supply', 'policy', 'news']);
export const IntelSeverity = z.enum(['info', 'watch', 'escalation', 'critical']);

export const IntelEvent = z.object({
  id: z.string(),
  kind: IntelKind,
  region: z.string(),
  country: Iso2.nullable(),
  severity: IntelSeverity,
  ts: z.string(),
  title: z.string(),
  summary: z.string(),
  source_url: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
});
